import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {
  StatsClient,
  ConversionStatResolution,
  ClientsClient,
  GetClient,
  GetConversionStat,
  GetCustomer,
  CustomersClient,
  PaginationResultOfGetCustomer
} from 'src/app/generated-client/generated-client';
import { Moment } from 'moment';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { MatFormField, MatLabel, MatInput, MatSuffix } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatButton } from '@angular/material/button';
import { StatsOverviewGraphComponent } from '../stats-overview-graph/stats-overview-graph.component';

@Component({
  selector: 'da-stats-overview',
  templateUrl: './stats-overview.component.html',
  styleUrls: ['./stats-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatButton,
    StatsOverviewGraphComponent
  ]
})
export class StatsOverviewComponent implements OnInit, OnDestroy {
  private statsClient = inject(StatsClient);
  private clientsClient = inject(ClientsClient);
  private customersClient = inject(CustomersClient);

  private _selectedResolution = signal(ConversionStatResolution.Month);
  set selectedResolution(value: ConversionStatResolution) {
    this._selectedResolution.set(value);
    this.queryData();
  }

  get selectedResolution(): ConversionStatResolution {
    return this._selectedResolution();
  }

  resolutions: ConversionStatResolution[] = Object.keys(ConversionStatResolution).map(k => <ConversionStatResolution>k);
  clients = signal<GetClient[]>([]);
  customers = signal<GetCustomer[]>([]);
  conversionStats = signal<GetConversionStat[]>(null);

  private _selectedClientId = signal<string>(null);
  set selectedClientId(value: string) {
    this._selectedClientId.set(value);
    this._selectedCustomerId.set(null);
    this.queryData();
  }

  get selectedClientId(): string {
    return this._selectedClientId();
  }

  private _selectedCustomerId = signal<string>(null);
  set selectedCustomerId(value: string) {
    this._selectedCustomerId.set(value);
    this._selectedClientId.set(null);
    this.queryData();
  }

  get selectedCustomerId(): string {
    return this._selectedCustomerId();
  }

  private _fromDate = signal<Moment>(null);
  set fromDate(value: Moment) {
    this._fromDate.set(value);
    this.queryData();
  }
  get fromDate() {
    return this._fromDate();
  }

  private _endDate = signal<Moment>(null);
  set endDate(value: Moment) {
    this._endDate.set(value);
    this.queryData();
  }
  get endDate() {
    return this._endDate();
  }

  private lastQuerySubscription: Subscription;
  private lookupSubscriptions: Subscription[] = [];

  ngOnInit() {
    this.lookupSubscriptions.push(this.clientsClient.getClients(null, null).subscribe(c => this.clients.set(c)));
    this.lookupSubscriptions.push(
      this.customersClient.getCustomers(null).subscribe((c: PaginationResultOfGetCustomer) => this.customers.set(c.data))
    );
    this.queryData();
  }

  ngOnDestroy() {
    this.lastQuerySubscription?.unsubscribe();
    this.lookupSubscriptions.forEach(subscription => subscription.unsubscribe());
  }

  clearDates() {
    this._fromDate.set(null);
    this._endDate.set(null);
    this.queryData();
  }

  currentMonth() {
    this._fromDate.set(moment().utc().startOf('month'));
    this._endDate.set(moment().utc().endOf('month'));
    this.queryData();
  }

  lastMonth() {
    this._fromDate.set(moment().utc().add(-1, 'M').startOf('month'));
    this._endDate.set(moment().utc().add(-1, 'M').endOf('month'));
    this.queryData();
  }

  private queryData() {
    if (this.lastQuerySubscription) {
      this.lastQuerySubscription.unsubscribe();
    }

    const fromDate = this._fromDate() ? this._fromDate().toDate() : undefined;
    const endDate = this._endDate() ? this._endDate().endOf('day').toDate() : undefined;
    const clientId = this.selectedClientId ? this.selectedClientId : undefined;
    const customerId = this.selectedCustomerId ? this.selectedCustomerId : undefined;
    this.lastQuerySubscription = this.statsClient
      .getStatsOverview(this.selectedResolution, fromDate, endDate, clientId, customerId)
      .subscribe(cs => this.conversionStats.set(cs));
  }
}
